import { Link, useParams } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { Page } from '@/components/Page'
import { formatKarachiDateTime } from '@/lib/dates'
import { formatPKR } from '@/lib/money'
import { openWorkspaceDb } from '@/offline/db'
import { getActiveWorkspaceId } from '@/features/workspace/workspace'
import { saleOutstanding } from '@/features/payments/outstanding'

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-center justify-between px-3 py-2 text-sm">
    <span className="text-slate-400">{label}</span>
    <span className="text-right font-medium tabular-nums text-slate-100">{value}</span>
  </div>
)

/** Device detail: identity, IMEIs, cost, sale, payments, chronological history. */
export function DeviceDetailPage() {
  const { deviceId = '' } = useParams()
  const ws = getActiveWorkspaceId()

  const device = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).devices.get(deviceId) : undefined), [ws, deviceId])
  const imeis = useLiveQuery(
    async () => (ws ? openWorkspaceDb(ws).device_imeis.where('device_id').equals(deviceId).toArray() : []),
    [ws, deviceId],
  )
  const purchase = useLiveQuery(
    async () => (ws ? openWorkspaceDb(ws).purchases.where('device_id').equals(deviceId).first() : undefined),
    [ws, deviceId],
  )
  const sale = useLiveQuery(
    async () => (ws ? openWorkspaceDb(ws).sales.where('device_id').equals(deviceId).first() : undefined),
    [ws, deviceId],
  )
  const payments = useLiveQuery(
    async () => (ws ? openWorkspaceDb(ws).payments.where('workspace_id').equals(ws).toArray() : []),
    [ws],
  )
  const category = useLiveQuery(
    async () => (ws && device ? openWorkspaceDb(ws).categories.get(device.category_id) : undefined),
    [ws, device],
  )

  if (!device) return <Page title="Device" note="Not found." />

  const cost = (purchase?.cost_paisa ?? 0) + (purchase?.direct_cost_paisa ?? 0)
  const salePayments = (payments ?? []).filter((p) => p.related_type === 'sale' && p.related_id === sale?.id)
  const outstanding = sale ? saleOutstanding(sale, payments ?? []) : 0

  const history = [
    purchase ? { at: purchase.purchased_at, label: `Purchased for ${formatPKR(purchase.cost_paisa)}` } : null,
    sale ? { at: sale.sold_at, label: `Sold for ${formatPKR(sale.sale_amount_paisa)} (received ${formatPKR(sale.received_paisa)})` } : null,
    ...salePayments.map((p) => ({ at: p.paid_at, label: `Payment received ${formatPKR(p.amount_paisa)}` })),
  ]
    .filter((x): x is { at: string; label: string } => x !== null)
    .sort((a, b) => a.at.localeCompare(b.at))

  return (
    <Page title={device.model} note={`${category?.name ?? '—'} • rev ${device.revision}`}>
      <div className="mb-3 flex gap-2">
        <Link to={`/inventory/${device.id}/edit`} className="flex-1 rounded-lg bg-sky-500 px-3 py-2 text-center text-sm font-semibold text-white">
          Edit
        </Link>
        {device.status === 'in_stock' ? (
          <Link to={`/inventory/${device.id}/sell`} className="flex-1 rounded-lg bg-emerald-500 px-3 py-2 text-center text-sm font-semibold text-white">
            Sell
          </Link>
        ) : null}
      </div>

      <div className="divide-y divide-slate-800 rounded-xl border border-slate-800 bg-slate-900/40">
        <Row label="Status" value={device.status === 'in_stock' ? 'In stock' : device.status === 'sold' ? 'Sold' : 'Returned'} />
        <Row label="Category" value={category?.name ?? '—'} />
        <Row label="Brand" value={device.brand_code ?? '—'} />
        {(imeis ?? []).map((im, i) => (
          <Row key={im.id} label={`IMEI ${i + 1}`} value={`${im.imei_normalized}${im.imei_pta_status !== 'unknown' ? ` (${im.imei_pta_status})` : ''}`} />
        ))}
        <Row label="PTA" value={device.pta_status} />
        {device.carrier_lock_status ? <Row label="Carrier lock" value={device.carrier_lock_status} /> : null}
        {device.mdm_status ? <Row label="MDM" value={device.mdm_status} /> : null}
        {device.storage ? <Row label="Storage" value={device.storage} /> : null}
        {device.ram ? <Row label="RAM" value={device.ram} /> : null}
        {device.condition ? <Row label="Condition" value={device.condition} /> : null}
        <Row label="Cost (incl. direct)" value={formatPKR(cost)} />
        {sale ? <Row label="Sale" value={formatPKR(sale.sale_amount_paisa)} /> : null}
        {sale ? <Row label="Outstanding" value={formatPKR(outstanding)} /> : null}
      </div>

      <h2 className="mb-1 mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">History</h2>
      {history.length === 0 ? (
        <p className="text-sm text-slate-500">No events yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {history.map((h, i) => (
            <li key={i} className="rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2 text-sm">
              <span className="text-slate-400">{formatKarachiDateTime(h.at)}</span>
              <span className="ml-2 text-slate-100">{h.label}</span>
            </li>
          ))}
        </ul>
      )}
    </Page>
  )
}
